import offlineDb from './db';

const TTL_MS = 72 * 60 * 60 * 1000; // 72 hours

function stamp(record) {
  if (!record || typeof record !== 'object') return record;
  return { ...record, _cachedAt: Date.now() };
}

function stampArray(records) {
  if (!Array.isArray(records)) return [];
  return records.map(stamp);
}

async function pruneStale(table) {
  try {
    const cutoff = Date.now() - TTL_MS;
    const staleKeys = await table.filter(r => r._cachedAt && r._cachedAt < cutoff).primaryKeys();
    if (staleKeys.length > 0) {
      await table.bulkDelete(staleKeys);
    }
  } catch {
    // Non-critical — silently skip if pruning fails
  }
}

class CacheWriter {
  async cachePatient(data) {
    if (!data?.id) return;
    await offlineDb.patients.put(stamp(data));
  }

  async cacheAppointments(data) {
    const items = Array.isArray(data) ? data : data?.items || [];
    if (!items.length) return;
    await offlineDb.appointments.bulkPut(stampArray(items));
    await pruneStale(offlineDb.appointments);
  }

  async cacheCases(data) {
    const items = Array.isArray(data) ? data : data?.items || [];
    if (!items.length) return;
    await offlineDb.cases.bulkPut(stampArray(items));
    await pruneStale(offlineDb.cases);
  }

  async cachePlansOfCare(data) {
    const items = Array.isArray(data) ? data : data?.items || [];
    if (!items.length) return;
    await offlineDb.plansOfCare.bulkPut(stampArray(items));
    await pruneStale(offlineDb.plansOfCare);
  }

  async cacheClinicalNotes(data) {
    const items = Array.isArray(data) ? data : data?.items || [];
    if (!items.length) return;
    await offlineDb.clinicalNotes.bulkPut(stampArray(items));
    await pruneStale(offlineDb.clinicalNotes);
  }

  async cacheClinicalNote(data) {
    if (!data?.id) return;
    await offlineDb.clinicalNotes.put(stamp(data));
  }

  async cacheEncounters(data) {
    const items = Array.isArray(data) ? data : data?.items || [];
    if (!items.length) return;
    await offlineDb.encounters.bulkPut(stampArray(items));
    await pruneStale(offlineDb.encounters);
  }

  async cacheEncounter(data) {
    if (!data?.id) return;
    await offlineDb.encounters.put(stamp(data));
  }

  async cacheFormResponses(data) {
    const items = Array.isArray(data) ? data : data?.items || [];
    if (!items.length) return;
    await offlineDb.formResponses.bulkPut(stampArray(items));
    await pruneStale(offlineDb.formResponses);
  }

  async cacheFormReports(data) {
    const items = Array.isArray(data) ? data : data?.items || [];
    if (!items.length) return;
    await offlineDb.formReports.bulkPut(stampArray(items));
    await pruneStale(offlineDb.formReports);
  }

  async cacheTemplates(data) {
    const items = Array.isArray(data) ? data : data?.items || [];
    if (!items.length) return;
    await offlineDb.templates.bulkPut(stampArray(items));
  }

  async cacheTemplate(data) {
    if (!data?.id) return;
    await offlineDb.templates.put(stamp(data));
  }

  async cacheOrganizationUsers(data) {
    const items = Array.isArray(data) ? data : data?.items || [];
    if (!items.length) return;
    await offlineDb.organizationUsers.bulkPut(stampArray(items));
  }

  async cacheAppointmentTypes(data) {
    const items = Array.isArray(data) ? data : data?.items || [];
    if (!items.length) return;
    await offlineDb.appointmentTypes.bulkPut(stampArray(items));
  }

  async cacheBillingRules(data) {
    const items = Array.isArray(data) ? data : data?.items || [];
    if (!items.length) return;
    await offlineDb.billingRules.bulkPut(stampArray(items));
  }

  async cacheInterventionCatalog(data) {
    const items = Array.isArray(data) ? data : data?.items || [];
    if (!items.length) return;
    await offlineDb.interventionCatalog.bulkPut(stampArray(items));
  }

  async cacheAppointment(data) {
    if (!data?.id) return;
    await offlineDb.appointments.put(stamp(data));
  }

  async cacheCase(data) {
    if (!data?.id) return;
    await offlineDb.cases.put(stamp(data));
  }

  async cacheFromEndpoint(endpoint, responseData) {
    if (!responseData) return;

    try {
      const path = endpoint.split('?')[0];

      if (path.match(/^\/clinical_note\/[^/]+$/)) {
        await this.cacheClinicalNote(responseData);
      } else if (path === '/clinical_note') {
        await this.cacheClinicalNotes(responseData);
      } else if (path.match(/^\/encounter\/[^/]+$/)) {
        await this.cacheEncounter(responseData);
      } else if (path === '/encounter') {
        await this.cacheEncounters(responseData);
      } else if (path.match(/^\/patient\/[^/]+$/)) {
        await this.cachePatient(responseData);
      } else if (path.match(/^\/appointment\/[^/]+$/)) {
        await this.cacheAppointment(responseData);
      } else if (path === '/appointment') {
        await this.cacheAppointments(responseData);
      } else if (path.match(/^\/case\/[^/]+$/)) {
        await this.cacheCase(responseData);
      } else if (path === '/case') {
        await this.cacheCases(responseData);
      } else if (path === '/plan-of-care') {
        await this.cachePlansOfCare(responseData);
      } else if (path.match(/^\/templates\/[^/]+$/)) {
        await this.cacheTemplate(responseData);
      } else if (path === '/templates') {
        await this.cacheTemplates(responseData);
      } else if (path === '/form-responses') {
        await this.cacheFormResponses(responseData);
      } else if (path === '/form-generated-reports') {
        await this.cacheFormReports(responseData);
      } else if (path === '/user') {
        await this.cacheOrganizationUsers(responseData);
      } else if (path === '/appointment_type') {
        await this.cacheAppointmentTypes(responseData);
      } else if (path === '/billing-rules') {
        await this.cacheBillingRules(responseData);
      } else if (path === '/intervention-catalog') {
        await this.cacheInterventionCatalog(responseData);
      } else if (path === '/user_solution') {
        await this.cacheUserSolutions(endpoint, responseData);
      } else if (path.match(/^\/organization-role\/[^/]+$/)) {
        await this.cacheOrganizationRole(path.split('/')[2], responseData);
      } else if (path === '/calendar-appointments') {
        await this.cacheCalendarFeed(responseData);
      } else if (path.match(/^\/organization\/[^/]+$/)) {
        if (responseData?.id) await offlineDb.organizations.put(stamp(responseData));
      } else if (path === '/organization_location') {
        // Shape-faithful exact-key echo (list shape varies by caller usage).
        await offlineDb.publicCache.put({ key: endpoint, data: responseData, _cachedAt: Date.now() });
      } else if (path === '/flow-sheet-entry') {
        await this.cacheFlowSheetEntries(responseData);
      } else if (path.match(/^\/flow-sheet-entry\/[^/]+$/)) {
        if (responseData?.id) await offlineDb.flowSheetEntries.put(stamp(responseData));
      } else if (path === '/flow-sheet-item-catalog') {
        await this.cacheFlowSheetCatalog(responseData);
      }
    } catch (e) {
      console.error('[CacheWriter] Failed to cache:', endpoint, e);
    }
  }

  // The six-key /calendar-appointments feed. IMPORTANT: this receives the
  // RAW response (cacheFromEndpoint runs on it before scheduling.js stitches
  // the array), so all six lists are present. One transaction per write.
  async cacheCalendarFeed(data) {
    if (!data || typeof data !== 'object' || !Array.isArray(data.appointments)) return;
    const t = Date.now();
    const stampList = (list) => (Array.isArray(list) ? list : [])
      .filter(r => r?.id != null)
      .map(r => ({ ...r, _cachedAt: t }));

    await offlineDb.transaction('rw',
      [offlineDb.appointments, offlineDb.calendarPatients, offlineDb.calendarCases,
       offlineDb.referringProviders, offlineDb.calendarUsers, offlineDb.appointmentTypes],
      async () => {
        // Never clobber an offline-modified appointment with a staler copy.
        const incoming = stampList(data.appointments);
        for (const appt of incoming) {
          const existing = await offlineDb.appointments.get(appt.id);
          if (existing?._localTouchedAt && (existing.updated_at || 0) > (appt.updated_at || 0)) continue;
          await offlineDb.appointments.put(appt);
        }
        await offlineDb.calendarPatients.bulkPut(stampList(data.patients));
        await offlineDb.calendarCases.bulkPut(stampList(data.cases));
        await offlineDb.referringProviders.bulkPut(stampList(data.referring_providers));
        await offlineDb.calendarUsers.bulkPut(stampList(data.users));
        await offlineDb.appointmentTypes.bulkPut(stampList(data.appointment_types));
      });
  }

  async cacheFlowSheetEntries(data) {
    const items = Array.isArray(data) ? data : data?.items || [];
    if (!items.length) return;
    await offlineDb.flowSheetEntries.bulkPut(stampArray(items));
    await pruneStale(offlineDb.flowSheetEntries);
  }

  async cacheFlowSheetCatalog(data) {
    const items = Array.isArray(data) ? data : data?.items || [];
    if (!items.length) return;
    await offlineDb.flowSheetCatalog.bulkPut(stampArray(items));
  }

  // Boot snapshot rows: the /user_solution and /organization-role responses
  // FeatureContext / PermissionContext need on an offline cold boot. Stored
  // in offlineSession alongside the /auth/me profile written by rememberUser.
  async cacheUserSolutions(endpoint, data) {
    const params = new URLSearchParams(endpoint.split('?')[1] || '');
    const organizationId = params.get('organization_id');
    const userId = params.get('user_id');
    if (!organizationId || !userId) return;
    await offlineDb.offlineSession.put({
      key: 'solutions',
      userId,
      organizationId,
      savedAt: Date.now(),
      data,
    });
  }

  async cacheOrganizationRole(roleId, data) {
    if (!roleId || !data) return;
    await offlineDb.offlineSession.put({
      key: 'orgRole',
      roleId,
      savedAt: Date.now(),
      data,
    });
  }

  async updateCacheMeta(key, data = {}) {
    await offlineDb.cacheMeta.put({
      key,
      lastFetched: Date.now(),
      ...data,
    });
  }

  async getCacheMeta(key) {
    return offlineDb.cacheMeta.get(key);
  }
}

export const cacheWriter = new CacheWriter();
